// src/components/Common/CategoriesManager.js
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../services/supabaseClient';
import { useImageUpload } from '../../hooks/useImageUpload';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Collapse,
  CircularProgress,
  Divider,
  Avatar,
  LinearProgress,
  Alert
} from '@mui/material';
import { 
  Add, 
  Delete as DeleteIcon, 
  Edit as EditIcon, 
  ExpandLess, 
  ExpandMore,
  Close as CloseIcon,
  PhotoCamera
} from '@mui/icons-material';

const CategoriesManager = ({ open, onClose }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editCategory, setEditCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryImage, setNewCategoryImage] = useState(null);
  const [openSubcategoryDialog, setOpenSubcategoryDialog] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [selectedParentId, setSelectedParentId] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  const textFieldRef = useRef(null);

  const { 
    uploading, 
    uploadProgress, 
    uploadSingleImage, 
    deleteImage 
  } = useImageUpload();

  const rtlTextFieldSx = {
    '& .MuiOutlinedInput-root': {
      '& .MuiOutlinedInput-notchedOutline legend': {
        textAlign: 'right',
        transformOrigin: 'top right',
        right: 12,
        left: 'auto'
      }
    },
    '& .MuiInputBase-input': {
      textAlign: 'right',
      direction: 'rtl',
      fontWeight: 'bold'
    }
  };

  const iconButtonContainerSx = {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: '2px', sm: '4px' },
    left: { xs: 8, sm: 16 },
    right: 'auto',
    minWidth: 'fit-content',
    flexShrink: 0
  };

  const iconButtonSx = {
    padding: { xs: '4px', sm: '8px' },
    minWidth: { xs: '32px', sm: '40px' },
    minHeight: { xs: '32px', sm: '40px' },
    '& svg': {
      fontSize: { xs: '18px', sm: '20px' }
    }
  };

  useEffect(() => {
    if (open) fetchCategories();
  }, [open]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError('');
      
      const { data: mainCategories, error: mainError } = await supabase
        .from('categories')
        .select('*')
        .is('parent_id', null)
        .order('name', { ascending: true });

      if (mainError) throw mainError;

      const { data: subCategories, error: subError } = await supabase
        .from('categories')
        .select('*')
        .not('parent_id', 'is', null)
        .order('name', { ascending: true });

      if (subError) throw subError;

      const categoriesWithSubs = mainCategories.map(cat => ({
        ...cat,
        subcategories: subCategories.filter(sub => sub.parent_id === cat.id)
      }));

      setCategories(categoriesWithSubs);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      setError('فشل في تحميل الفئات: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleImageSelect = (event, isEdit = false) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setError('نوع الملف غير مدعوم. يرجى اختيار صورة بصيغة JPG، PNG، GIF أو WebP');
        return;
      }
      
      // Validate file size (5MB max)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setError('حجم الملف كبير جداً. الحد الأقصى 5 ميجابايت');
        return;
      }

      setError('');
      if (isEdit) {
        setEditCategory(prev => ({ ...prev, newImage: file }));
      } else {
        setNewCategoryImage(file);
      }
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      setError('يرجى إدخال اسم الفئة');
      return;
    }

    try {
      setError('');
      let imageUrl = null;

      // Upload image if selected
      if (newCategoryImage) {
        imageUrl = await uploadSingleImage(newCategoryImage, 'categories');
      }

      const { data, error } = await supabase
        .from('categories')
        .insert([{ 
          name: newCategoryName.trim(), 
          parent_id: selectedParentId,
          image_url: imageUrl
        }])
        .select()
        .single();

      if (error) throw error;

      if (selectedParentId) {
        setCategories(categories.map(cat => 
          cat.id === selectedParentId 
            ? { ...cat, subcategories: [...cat.subcategories, data] } 
            : cat
        ));
        setOpenSubcategoryDialog(false);
        setSelectedParentId(null);
      } else {
        setCategories([...categories, { ...data, subcategories: [] }]);
      }

      // Reset form
      setNewCategoryName('');
      setNewCategoryImage(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Failed to add category:', error);
      setError('فشل في إضافة الفئة: ' + error.message);
    }
  };

  const handleUpdateCategory = async () => {
    if (!editCategory?.name?.trim()) {
      setError('يرجى إدخال اسم الفئة');
      return;
    }

    try {
      setError('');
      let imageUrl = editCategory.image_url;

      // Upload new image if selected
      if (editCategory.newImage) {
        // Delete old image if exists
        if (editCategory.image_url) {
          try {
            await deleteImage(editCategory.image_url, 'categories');
          } catch (deleteError) {
            console.warn('Failed to delete old image:', deleteError);
          }
        }
        
        imageUrl = await uploadSingleImage(editCategory.newImage, 'categories');
      }

      const { error } = await supabase
        .from('categories')
        .update({ 
          name: editCategory.name.trim(),
          image_url: imageUrl
        })
        .eq('id', editCategory.id);

      if (error) throw error;

      setCategories(categories.map(cat => {
        if (editCategory.parent_id) {
          return cat.id === editCategory.parent_id 
            ? {
                ...cat,
                subcategories: cat.subcategories.map(sub =>
                  sub.id === editCategory.id 
                    ? { ...sub, name: editCategory.name, image_url: imageUrl } 
                    : sub
                )
              }
            : cat;
        }
        return cat.id === editCategory.id 
          ? { ...cat, name: editCategory.name, image_url: imageUrl } 
          : cat;
      }));
      
      setEditCategory(null);
    } catch (error) {
      console.error('Failed to update category:', error);
      setError('فشل في تحديث الفئة: ' + error.message);
    }
  };

  const handleDeleteCategory = async (category, parentId = null) => {
    if (!window.confirm(`هل أنت متأكد من حذف "${category.name}"؟`)) return;

    try {
      setError('');

      // Delete associated image if exists
      if (category.image_url) {
        try {
          await deleteImage(category.image_url, 'categories');
        } catch (deleteError) {
          console.warn('Failed to delete category image:', deleteError);
        }
      }

      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', category.id);

      if (error) throw error;

      parentId 
        ? setCategories(categories.map(cat => 
            cat.id === parentId 
              ? { ...cat, subcategories: cat.subcategories.filter(sub => sub.id !== category.id) } 
              : cat
          ))
        : setCategories(categories.filter(cat => cat.id !== category.id));
    } catch (error) {
      console.error('Failed to delete category:', error);
      setError('فشل في حذف الفئة: ' + error.message);
    }
  };

  const handleAddSubcategory = (categoryId) => {
    setSelectedParentId(categoryId);
    setOpenSubcategoryDialog(true);
    setNewCategoryName('');
    setNewCategoryImage(null);
    setError('');
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ 
        fontWeight: 'bold', 
        textAlign: 'right', 
        direction: 'rtl',
        position: 'relative',
        py: 2,
        pr: 5
      }}>
        إدارة الفئات
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 10,
            left: 22,
            color: '#0f3662',
            padding: '0.2rem',
            borderRadius: '25%', 
            border: '2px solid #a72c2a'
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, textAlign: 'right' }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {uploading && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ textAlign: 'right', mb: 1 }}>
              جاري رفع الصورة... {Math.round(uploadProgress)}%
            </Typography>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mb: 2 }}>
            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 'bold', 
                textAlign: 'right',
                mb: 3
              }}>
                إدارة الفئات الرئيسية
              </Typography>

              {/* Add Main Category Section */}
              <Box sx={{ mb: 2 }}>
                {/* Image selection for new category - moved to show selected image info */}
                {!selectedParentId && newCategoryImage && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 2 }}>
                    <Typography variant="body2" sx={{ mr: 2 }}>
                      تم اختيار: {newCategoryImage.name}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  gap: 1
                }}>
                  {/* Upload Photo Button - moved first, minimized width */}
                  {!selectedParentId && (
                    <>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={(e) => handleImageSelect(e)}
                        accept="image/*"
                        style={{ display: 'none' }}
                      />
                      <Button
                        variant="outlined"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        sx={{ 
                          fontWeight: 'bold',
                          height: '56px', // Match TextField height
                          minWidth: '75px', // Minimized width
                          width: '75px', // Fixed width for consistency
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '2px',
                          fontSize: '0.75rem', // Smaller font
                          p: '4px'
                        }}
                      >
                        <PhotoCamera sx={{ fontSize: '18px' }} />
                        رفع صورة
                      </Button>
                    </>
                  )}
                  
                  <TextField
                    ref={textFieldRef}
                    fullWidth
                    placeholder={selectedParentId ? "جاري إضافة قسم فرعي..." : "اسم الفئة الجديدة"}
                    value={selectedParentId ? '' : newCategoryName}
                    onChange={(e) => {
                      if (!selectedParentId) {
                        setNewCategoryName(e.target.value);
                      }
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !selectedParentId && newCategoryName.trim()) {
                        handleAddCategory();
                      }
                    }}
                    disabled={!!selectedParentId || uploading}
                    sx={{
                      ...rtlTextFieldSx,
                      '& .MuiInputBase-input::placeholder': {
                        opacity: selectedParentId ? 0.6 : 1,
                        color: selectedParentId ? 'text.secondary' : 'text.primary'
                      }
                    }}
                  />
                </Box>

                {/* "إضافة" Button - now takes full width of text input + photo button */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  mt: 1,
                  width: '100%'
                }}>
                  <Button 
                    variant="contained" 
                    onClick={handleAddCategory}
                    disabled={!!selectedParentId || !newCategoryName.trim() || uploading}
                    sx={{ 
                      fontWeight: 'bold',
                      width: '100%' // Full width
                    }}
                  >
                    إضافة
                  </Button>
                </Box>
              </Box>

              {/* Categories List */}
              <List component={Paper} variant="outlined" sx={{ p: 0 }}>
                {categories.length > 0 ? (
                  categories.map((category) => (
                    <React.Fragment key={category.id}>
                      <ListItem sx={{ 
                        pr: { xs: 2, sm: 3 },
                        pl: { xs: '100px', sm: '120px' },
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative'
                      }}>
                        <IconButton 
                          onClick={() => handleAddSubcategory(category.id)}
                          sx={{
                            ...iconButtonSx,
                            position: 'absolute',
                            right: { xs: '6px', sm: '10px' },
                            zIndex: 1,
                            color: 'primary.main'
                          }}
                          size="small"
                        >
                          <Add />
                        </IconButton>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, pr: { xs: '22px', sm: '30px' } }}>
                          <ListItemText 
                            primary={category.name} 
                            primaryTypographyProps={{ 
                              fontWeight: 'bold', 
                              textAlign: 'right',
                              sx: { 
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                direction: 'rtl',
                                unicodeBidi: 'embed'
                              }
                            }}
                          />
                        </Box>
                        
                        <ListItemSecondaryAction sx={iconButtonContainerSx}>
                          <IconButton 
                            onClick={() => handleToggleCategory(category.id)}
                            sx={iconButtonSx}
                            size="small"
                          >
                            {expandedCategories[category.id] ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                          <IconButton 
                            onClick={() => handleDeleteCategory(category)}
                            sx={iconButtonSx}
                            size="small"
                          >
                            <DeleteIcon color="error" />
                          </IconButton>
                          <IconButton 
                            onClick={() => setEditCategory({ ...category })}
                            sx={iconButtonSx}
                            size="small"
                          >
                            <EditIcon color="primary" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                      
                      <Collapse in={expandedCategories[category.id]} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding sx={{ bgcolor: 'action.hover' }}>
                          {category.subcategories.length > 0 ? (
                            category.subcategories.map((subcat) => (
                              <ListItem 
                                key={subcat.id} 
                                sx={{ 
                                  pl: { xs: '80px', sm: '100px' },
                                  pr: { xs: 6, sm: 7 }
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                  <ListItemText 
                                    primary={subcat.name} 
                                    primaryTypographyProps={{ 
                                      fontWeight: 'bold', 
                                      textAlign: 'right',
                                      sx: { 
                                        pr: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        direction: 'rtl',
                                        unicodeBidi: 'embed'
                                      }
                                    }}
                                  />
                                </Box>
                                
                                <ListItemSecondaryAction sx={iconButtonContainerSx}>
                                  <IconButton 
                                    onClick={() => handleDeleteCategory(subcat, category.id)}
                                    sx={iconButtonSx}
                                    size="small"
                                  >
                                    <DeleteIcon color="error" />
                                  </IconButton>
                                  <IconButton 
                                    onClick={() => setEditCategory({ 
                                      ...subcat, 
                                      parent_id: category.id 
                                    })}
                                    sx={iconButtonSx}
                                    size="small"
                                  >
                                    <EditIcon color="primary" />
                                  </IconButton>
                                </ListItemSecondaryAction>
                              </ListItem>
                            ))
                          ) : (
                            <ListItem sx={{ pl: 4 }}>
                              <ListItemText 
                                primary="لا توجد أقسام فرعية" 
                                primaryTypographyProps={{ fontWeight: 'bold', textAlign: 'right' }}
                              />
                            </ListItem>
                          )}
                        </List>
                      </Collapse>
                      <Divider />
                    </React.Fragment>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText 
                      primary="لا توجد فئات مضافة" 
                      primaryTypographyProps={{ fontWeight: 'bold', textAlign: 'right' }}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Box>
        )}
      </DialogContent>

      {/* Edit Dialog */}
      <Dialog
        open={!!editCategory}
        onClose={() => setEditCategory(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', textAlign: 'right', direction: 'rtl' }}>
          تعديل الفئة
        </DialogTitle>
        <DialogContent>
          {/* Current Image Display */}
          {(editCategory?.image_url || editCategory?.newImage) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Avatar 
                src={editCategory.newImage 
                  ? URL.createObjectURL(editCategory.newImage)
                  : editCategory.image_url
                } 
                sx={{ width: 80, height: 80 }}
              />
            </Box>
          )}

          {/* Image Upload */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <input
              type="file"
              ref={editFileInputRef}
              onChange={(e) => handleImageSelect(e, true)}
              accept="image/*"
              style={{ display: 'none' }}
            />
            <Button
              variant="outlined"
              startIcon={<PhotoCamera />}
              onClick={() => editFileInputRef.current?.click()}
              disabled={uploading}
            >
              {editCategory?.image_url || editCategory?.newImage ? 'تغيير الصورة' : 'إضافة صورة'}
            </Button>
          </Box>

          {editCategory?.newImage && (
            <Typography variant="body2" sx={{ textAlign: 'center', mb: 2 }}>
              تم اختيار صورة جديدة: {editCategory.newImage.name}
            </Typography>
          )}

          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="اسم الفئة"
            value={editCategory?.name || ''}
            onChange={(e) => setEditCategory({ ...editCategory, name: e.target.value })}
            InputLabelProps={{
              sx: {
                fontWeight: 'bold',
                textAlign: 'right',
                transformOrigin: 'top right',
                right: 26,
                left: 'auto'
              }
            }}
            sx={rtlTextFieldSx}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setEditCategory(null)} 
            sx={{ fontWeight: 'bold' }}
            disabled={uploading}
          >
            إلغاء
          </Button>
          <Button 
            onClick={handleUpdateCategory} 
            color="primary" 
            sx={{ fontWeight: 'bold' }}
            disabled={uploading}
          >
            حفظ التغييرات
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subcategory Dialog */}
      <Dialog
        open={openSubcategoryDialog}
        onClose={() => {
          setOpenSubcategoryDialog(false);
          setSelectedParentId(null);
          setNewCategoryName('');
          setNewCategoryImage(null);
          setError('');
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', textAlign: 'right', direction: 'rtl' }}>
          إضافة قسم فرعي
        </DialogTitle>
        <DialogContent>
          {/* Image selection for subcategory */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <input
              type="file"
              onChange={(e) => handleImageSelect(e)}
              accept="image/*"
              style={{ display: 'none' }}
              id="subcategory-image-input"
            />
            <Button
              variant="outlined"
              startIcon={<PhotoCamera />}
              onClick={() => document.getElementById('subcategory-image-input')?.click()}
              disabled={uploading}
            >
              اختيار صورة
            </Button>
            {newCategoryImage && (
              <Typography variant="body2" sx={{ ml: 2 }}>
                تم اختيار: {newCategoryImage.name}
              </Typography>
            )}
          </Box>

          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="اسم القسم الفرعي"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
            InputLabelProps={{
              sx: {
                fontWeight: 'bold',
                textAlign: 'right',
                transformOrigin: 'top right',
                right: 25,
                left: 'auto'
              }
            }}
            sx={rtlTextFieldSx}
            disabled={uploading}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setOpenSubcategoryDialog(false);
              setSelectedParentId(null);
              setNewCategoryName('');
              setNewCategoryImage(null);
              setError('');
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }
            }} 
            sx={{ fontWeight: 'bold' }}
            disabled={uploading}
          >
            إلغاء
          </Button>
          <Button 
            onClick={handleAddCategory} 
            color="primary" 
            sx={{ fontWeight: 'bold' }}
            disabled={uploading || !newCategoryName.trim()}
          >
            إضافة
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default CategoriesManager;
